import React, { useState, useEffect } from 'react';
import { getLegacyApiUrl } from '../utils/apiUtils';
import { useNavigate } from 'react-router-dom';
import LecturerSidebar from '../components/LecturerSidebar';
import { useAuth } from '../contexts/AuthContext';

const ManualAttendanceMark = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    semester: '',
    year: '',
    session_type: '',
    module_code: '',
    groups: [],
    location: '',
    date: '',
    class_start_time: '',
    class_end_time: '',
    description: '',
    academic_year: ''
  });

  // State for dropdown options
  const [semesters, setSemesters] = useState([]);
  const [modules, setModules] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});

  // Fetch initial data when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingData(true);
        
        const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
        
        console.log('Fetching data for lecturer ID:', lecturerId);
        
        // Fetch lecturer's semesters
        const semesterResponse = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/semesters`);
        const semesterData = await semesterResponse.json();
        
        console.log('Semester API response:', semesterData);
        
        if (semesterData.success) {
          setSemesters(semesterData.semesters);
        } else {
          console.error('Failed to fetch semesters:', semesterData.error);
        }
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchInitialData();
  }, [currentUser]);

  // Fetch modules when semester changes
  useEffect(() => {
    const fetchModules = async () => {
      if (formData.semester) {
        try {
          const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
          console.log('Fetching modules for lecturer:', lecturerId, 'semester:', formData.semester);
          
          const response = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/modules/${formData.semester}`);
          const data = await response.json();
          
          console.log('Modules API response:', data);
          
          if (data.success) {
            setModules(data.modules);
          } else {
            console.error('Failed to fetch modules:', data.error);
            setModules([]);
          }
        } catch (error) {
          console.error('Error fetching modules:', error);
          setModules([]);
        }
      } else {
        setModules([]);
      }
    };

    fetchModules();
  }, [formData.semester, currentUser]);

  // Fetch groups when module changes
  useEffect(() => {
    const fetchModuleData = async () => {
      if (formData.module_code) {
        try {
          // Fetch groups
          setLoadingGroups(true);
          console.log('Fetching groups for module:', formData.module_code);
          
          const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
          const groupsResponse = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/module/${formData.module_code}/groups?semester=${formData.semester}&year=${formData.year}&academic_year=${formData.academic_year}`);
          const groupsData = await groupsResponse.json();
          
          console.log('Groups API response:', groupsData);
          
          if (groupsData.success) {
            setAvailableGroups(groupsData.groups);
          } else {
            console.error('Failed to fetch groups:', groupsData.error);
            setAvailableGroups([]);
          }
          
        } catch (error) {
          console.error('Error fetching module data:', error);
          setAvailableGroups([]);
        } finally {
          setLoadingGroups(false);
        }
      } else {
        setAvailableGroups([]);
      }
    };

    fetchModuleData();
  }, [formData.module_code, formData.semester, formData.year, formData.academic_year, currentUser?.id]);

  // Fetch students when groups are selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (formData.groups.length > 0 && formData.module_code) {
        try {
          setLoadingStudents(true);
          console.log('Fetching students for groups:', formData.groups, 'module:', formData.module_code);
          
          // Get authentication token
          const token = localStorage.getItem('token');
          
          const response = await fetch(`${getLegacyApiUrl()}/students/by-groups-and-module`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              group_ids: formData.groups,
              module_code: formData.module_code
            })
          });
          
          const data = await response.json();
          
          console.log('Students API response:', data);
          
          if (data.success) {
            setStudents(data.students);
            // Initialize attendance data for all students with no default selection
            const initialAttendance = {};
            data.students.forEach(student => {
              initialAttendance[student.id] = null; // No default selection
            });
            setAttendanceData(initialAttendance);
          } else {
            console.error('Failed to fetch students:', data.error);
            setStudents([]);
            setAttendanceData({});
          }
          
        } catch (error) {
          console.error('Error fetching students:', error);
          setStudents([]);
          setAttendanceData({});
        } finally {
          setLoadingStudents(false);
        }
      } else {
        setStudents([]);
        setAttendanceData({});
      }
    };

    fetchStudents();
  }, [formData.groups, formData.module_code]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset module_code when semester changes
    if (name === 'semester') {
      setFormData(prev => ({
        ...prev,
        module_code: '',
        groups: []
      }));
    }

    // Reset groups when module changes
    if (name === 'module_code') {
      setFormData(prev => ({
        ...prev,
        groups: []
      }));
    }
  };

  const handleGroupToggle = (groupId) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(groupId)
        ? prev.groups.filter(id => id !== groupId)
        : [...prev.groups, groupId]
    }));
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: prev[studentId] === status ? null : status // Toggle behavior
    }));
  };

  const handleSubmitAttendance = async () => {
    try {
      // Validate required fields
      const requiredFields = ['semester', 'year', 'session_type', 'module_code', 'date', 'class_start_time', 'class_end_time', 'academic_year'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        alert(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }

      if (formData.groups.length === 0) {
        alert('Please select at least one group');
        return;
      }

      if (students.length === 0) {
        alert('No students found for the selected groups and module');
        return;
      }

      // Validate time logic
      if (formData.class_start_time >= formData.class_end_time) {
        alert('Class end time must be after class start time');
        return;
      }

      // Prepare attendance data for submission - only students marked as present
      const presentStudents = students.filter(student => 
        attendanceData[student.id] === 'present'
      );

      if (presentStudents.length === 0) {
        alert('Please mark at least one student as present before submitting');
        return;
      }

      const attendanceRecords = presentStudents.map(student => ({
        student_id: student.id,
        status: 'present',
        marked_at: new Date().toISOString()
      }));

      const submissionData = {
        ...formData,
        lecturer_id: currentUser?.id,
        session_datetime: `${formData.date}T${formData.class_start_time}:00`,
        module_name: formData.module_code,
        attendance_records: attendanceRecords,
        marking_method: 'manual'
      };

      console.log('Submitting manual attendance:', submissionData);

      // Get authentication token
      const token = localStorage.getItem('token');

      // Submit to backend
      const response = await fetch(`${getLegacyApiUrl()}/attendance/manual-mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (result.success) {
        alert('Attendance marked successfully!');
        navigate('/lecturer-dashboard');
      } else {
        alert(`Failed to mark attendance: ${result.error}`);
      }
      
    } catch (error) {
      console.error('Error submitting attendance:', error);
      alert(`Error marking attendance: ${error.message}`);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F6FA' }}>
      <LecturerSidebar />
      
      <div style={{ marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh', background: '#F5F6FA', position: 'relative' }}>
        {/* Page Title */}
        <div style={{ padding: '32px 40px 20px 40px' }}>
          <h1 style={{ fontSize: 36, fontFamily: 'Outfit, sans-serif', fontWeight: '700', color: '#131A1A', margin: 0 }}>
            Mark Manual Attendance
          </h1>
          <p style={{ fontSize: 16, color: '#798AA3', margin: '8px 0 0 0', fontFamily: 'Outfit, sans-serif' }}>
            Manually mark attendance for your class session
          </p>
        </div>

        <div style={{ padding: '0 40px 40px 40px' }}>
          {loadingData && (
            <div style={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '1px solid #bbdefb',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              ⟲ Loading lecturer data...
            </div>
          )}

          {/* Session Configuration Form */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
            borderLeft: '8px solid #F04915'
          }}>
            <h2 style={{ fontSize: 24, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: '#131A1A', marginBottom: '24px' }}>
              Session Configuration
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24, marginBottom: 32 }}>
              {/* Semester */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Semester *</label>
                <div style={{ position: 'relative' }}>
                  <select
                    name="semester"
                    value={formData.semester}
                    onChange={handleInputChange}
                    disabled={loadingData}
                    style={{
                      width: '100%',
                      height: 40,
                      border: '1px solid #D5D9DD',
                      borderRadius: 5,
                      padding: '0 16px',
                      fontSize: 14,
                      color: '#252C58',
                      backgroundColor: 'white',
                      cursor: loadingData ? 'not-allowed' : 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none'
                    }}
                  >
                    <option value="">Select semester</option>
                    {semesters.map((semester, index) => (
                      <option key={index} value={semester.value}>{semester.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Year */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Year *</label>
                <select
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select year</option>
                  <option value="1">Year 1</option>
                  <option value="2">Year 2</option>
                  <option value="3">Year 3</option>
                  <option value="4">Year 4</option>
                </select>
              </div>

              {/* Session Type */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Session Type *</label>
                <select
                  name="session_type"
                  value={formData.session_type}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select session type</option>
                  <option value="lecture">Lecture</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="practical">Practical</option>
                  <option value="laboratory">Laboratory</option>
                  <option value="workshop">Workshop</option>
                </select>
              </div>

              {/* Module Code */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Module Code *</label>
                <select
                  name="module_code"
                  value={formData.module_code}
                  onChange={handleInputChange}
                  disabled={!formData.semester || modules.length === 0}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: (!formData.semester || modules.length === 0) ? 'not-allowed' : 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">
                    {!formData.semester 
                      ? "Select semester first" 
                      : modules.length === 0 
                        ? "No modules available" 
                        : "Select module code"
                    }
                  </option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.code}>
                      {module.code} - {module.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Academic Year */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Academic Year *</label>
                <select
                  name="academic_year"
                  value={formData.academic_year}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select academic year</option>
                  <option value="2023-2024">2023-2024</option>
                  <option value="2024-2025">2024-2025</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                  <option value="2027-2028">2027-2028</option>
                  <option value="2028-2029">2028-2029</option>
                  <option value="2029-2030">2029-2030</option>
                </select>
              </div>

              {/* Location */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Location</label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select country</option>
                  <option value="LK">Sri Lanka</option>
                  <option value="UK">United Kingdom</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="IN">India</option>
                  <option value="MY">Malaysia</option>
                  <option value="SG">Singapore</option>
                  <option value="ID">Indonesia</option>
                  <option value="TH">Thailand</option>
                  <option value="VN">Vietnam</option>
                  <option value="PH">Philippines</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
              </div>

              {/* Class Start Time */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Class Start Time *</label>
                <input
                  type="time"
                  name="class_start_time"
                  value={formData.class_start_time}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
              </div>

              {/* Class End Time */}
              <div>
                <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>Class End Time *</label>
                <input
                  type="time"
                  name="class_end_time"
                  value={formData.class_end_time}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            {/* Groups Multi-select */}
            <div style={{ marginBottom: 32 }}>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block', fontWeight: '500' }}>
                Groups {loadingGroups && <span style={{ color: '#1976d2' }}>(Loading...)</span>}
              </label>
              <div style={{
                border: '1px solid #D5D9DD',
                borderRadius: 5,
                minHeight: 40,
                maxHeight: 120,
                overflowY: 'auto',
                padding: 8,
                backgroundColor: 'white'
              }}>
                {!formData.module_code ? (
                  <div style={{ padding: 8, color: '#666' }}>Select a module first</div>
                ) : availableGroups.length === 0 ? (
                  <div style={{ padding: 8, color: '#666' }}>
                    {loadingGroups ? 'Loading groups...' : 'No groups available for this module'}
                  </div>
                ) : (
                  availableGroups.map((group) => (
                    <div key={group.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 8px',
                      margin: '2px 0',
                      borderRadius: 3,
                      cursor: 'pointer',
                      backgroundColor: formData.groups.includes(group.id) ? '#e3f2fd' : 'transparent',
                      border: formData.groups.includes(group.id) ? '1px solid #1976d2' : '1px solid transparent'
                    }}
                    onClick={() => handleGroupToggle(group.id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.groups.includes(group.id)}
                        onChange={() => handleGroupToggle(group.id)}
                        style={{ marginRight: 8 }}
                      />
                      <span>{group.name} (Year {group.year})</span>
                    </div>
                  ))
                )}
              </div>
              {formData.groups.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                  {formData.groups.length} group(s) selected
                </div>
              )}
            </div>
          </div>

          {/* Student Attendance List */}
          {students.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: '32px',
              marginBottom: '32px',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
              borderLeft: '8px solid #252C58'
            }}>
              <h2 style={{ fontSize: 24, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: '#131A1A', marginBottom: '24px' }}>
                Mark Attendance ({students.length} students)
              </h2>

              {/* Quick Actions */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#F8FAFC',
                borderRadius: '8px',
                border: '1px solid #E2E8F0'
              }}>
                <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500', marginRight: '8px' }}>
                  Quick Actions:
                </span>
                <button
                  onClick={() => {
                    const newAttendance = {};
                    students.forEach(student => {
                      newAttendance[student.id] = 'present';
                    });
                    setAttendanceData(newAttendance);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #16A34A',
                    backgroundColor: '#DCFCE7',
                    color: '#16A34A',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  ✅ Mark All Present
                </button>
                <button
                  onClick={() => {
                    const newAttendance = {};
                    students.forEach(student => {
                      newAttendance[student.id] = null; // Clear all selections
                    });
                    setAttendanceData(newAttendance);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #6B7280',
                    backgroundColor: '#F9FAFB',
                    color: '#6B7280',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Clear All
                </button>
              </div>

              {loadingStudents ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#798AA3' }}>
                  Loading students...
                </div>
              ) : (
                <div style={{ marginBottom: '24px' }}>
                  {/* Students Summary */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#F0F9FF',
                    borderRadius: '8px',
                    border: '1px solid #BAE6FD'
                  }}>
                    <div style={{ fontSize: '14px', color: '#0369A1', fontWeight: '500' }}>
                      📊 Total Students: {students.length}
                    </div>
                    <div style={{ fontSize: '14px', color: '#0369A1' }}>
                      ✅ Present: {Object.values(attendanceData).filter(status => status === 'present').length}
                    </div>
                    <div style={{ fontSize: '14px', color: '#0369A1' }}>
                      🎯 Selected Groups: {formData.groups.length}
                    </div>
                    <div style={{ fontSize: '14px', color: '#0369A1' }}>
                      📚 Module: {formData.module_code}
                    </div>
                  </div>

                  {/* Students Grid */}
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {students.map((student) => (
                      <div key={student.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '20px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease',
                        ':hover': {
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                        }
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            {/* Student Avatar */}
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: '#F04915',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '600',
                              fontSize: '16px'
                            }}>
                              {student.name ? student.name.charAt(0).toUpperCase() : 'S'}
                            </div>
                            
                            {/* Student Name */}
                            <div>
                              <div style={{ fontSize: '18px', fontWeight: '600', color: '#131A1A' }}>
                                {student.name}
                              </div>
                              <div style={{ fontSize: '14px', color: '#798AA3', fontWeight: '500' }}>
                                {student.student_id}
                              </div>
                            </div>
                          </div>
                          
                          {/* Student Details */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginTop: '8px' }}>
                            <div style={{ fontSize: '13px', color: '#6B7280' }}>
                              📧 {student.email}
                            </div>
                            {student.group_name && (
                              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                                👥 Group: {student.group_name}
                              </div>
                            )}
                            {student.year && (
                              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                                📅 Year {student.year}
                              </div>
                            )}
                            {student.semester && (
                              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                                📖 Semester {student.semester}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Attendance Options */}
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <button 
                            onClick={() => handleAttendanceChange(student.id, 'present')}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              cursor: 'pointer',
                              padding: '10px 16px',
                              borderRadius: '8px',
                              backgroundColor: attendanceData[student.id] === 'present' ? '#DCFCE7' : '#F9FAFB',
                              border: attendanceData[student.id] === 'present' ? '2px solid #16A34A' : '2px solid #E5E7EB',
                              transition: 'all 0.2s ease',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: attendanceData[student.id] === 'present' ? '#16A34A' : '#6B7280'
                            }}
                          >
                            <span style={{ marginRight: '6px' }}>
                              {attendanceData[student.id] === 'present' ? '✅' : '⭕'}
                            </span>
                            {attendanceData[student.id] === 'present' ? 'Present' : 'Mark Present'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <button 
              onClick={handleBack}
              style={{
                padding: '12px 32px',
                border: '1px solid #F04915',
                borderRadius: 8,
                background: 'white',
                color: '#F04915',
                fontSize: 16,
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Outfit, sans-serif'
              }}
            >
              Back
            </button>
            
            <button 
              onClick={handleSubmitAttendance}
              disabled={students.length === 0}
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: 8,
                background: students.length === 0 ? '#ccc' : '#F04915',
                color: 'white',
                fontSize: 16,
                fontWeight: '500',
                cursor: students.length === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'Outfit, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Submit Attendance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualAttendanceMark;
